import { useViewContext } from "@editor-providers";
import { useIsPdf } from "@library/components/pdf";
import { throttle } from "@library/shared/utils/throttle";
import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTimelineCtx } from "../ctx/timeline-ctx";
import type {
  ITimelinePathUpdateListener,
  ITimelineScrollAnimCtx,
  ITimelineScrollListener,
} from "../ctx/timeline-scroll-anim-ctx";
import type { ITimelineEditorData } from "../types";

interface IProps {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  editorData?: ITimelineEditorData;
}

export const useTimelineScrollAnim = ({
  containerRef,
  editorData,
}: IProps): ITimelineScrollAnimCtx => {
  const { isVertical } = useTimelineCtx();

  const { view } = useViewContext();
  const isPdf = useIsPdf();

  const canAnimate = useRef<boolean>(!isPdf && view !== "thumbnail");
  const scale = useRef<{ x: number; y: number }>({ x: 1, y: 1 });

  const scrollListeners = useRef<Map<string, ITimelineScrollListener>>(
    new Map([]),
  );
  const pathUpdateListeners = useRef<Map<string, ITimelinePathUpdateListener>>(
    new Map([]),
  );

  const handleResizeUpdates = useCallback(
    throttle(() => {
      // Possible optimization here,
      // right now we're calling all of the paths in timeline to update
      // we could just try calling only the one changing and prev&next ones
      pathUpdateListeners.current.forEach((listener) => {
        listener.callback({});
      });
    }, 200),
    [],
  );

  const resizeObserverRef = useRef(new ResizeObserver(handleResizeUpdates));

  // Unfortunately we do need them when not in view as well
  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const comboEl = containerRef.current;
    if (!comboEl || !isVertical) return;

    // Calculate scale
    const comboRect = comboEl.getBoundingClientRect();
    const scaleX = comboRect.width / comboEl.offsetWidth;
    const scaleY = comboRect.height / comboEl.offsetHeight;
    scale.current = {
      x: 1 / (Number.isNaN(scaleX) ? 1 : scaleX),
      y: 1 / (Number.isNaN(scaleY) ? 1 : scaleY),
    };

    if (isPdf) {
      canAnimate.current = false;
      return;
    }

    canAnimate.current = true;

    const designSystemScrollContainer = comboEl.closest(
      "ngx-design-system-dialog .editorV4Preview",
    );
    const attachScrollListener = () => {
      hasScrollListener = true;
      if (designSystemScrollContainer instanceof HTMLElement) {
        designSystemScrollContainer.addEventListener(
          "scroll",
          handleScrollEvent,
        );
      }
      window.addEventListener("scroll", handleScrollEvent);
    };

    const removeScrollListener = () => {
      hasScrollListener = false;
      if (designSystemScrollContainer instanceof HTMLElement) {
        designSystemScrollContainer.removeEventListener(
          "scroll",
          handleScrollEvent,
        );
        return;
      }
      window.removeEventListener("scroll", handleScrollEvent);
    };

    let hasScrollListener = false;
    const handleScrollEvent = () => {
      scrollListeners.current.forEach((listener) => {
        listener.callback({
          scrollTop: window.scrollY,
        });
      });
    };

    const interObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      if (!entry.isIntersecting) {
        removeScrollListener();
        return;
      }

      if (hasScrollListener) return;
      attachScrollListener();
    });
    interObserver.observe(comboEl);

    return () => {
      interObserver.disconnect();
      resizeObserverRef.current.unobserve(comboEl);
      window.removeEventListener("scroll", handleScrollEvent);
      if (hasScrollListener) {
        removeScrollListener();
        hasScrollListener = false;
      }
    };
  }, [isVertical, view, isPdf, editorData?.isThumbnailsGridView]);

  return useMemo(() => {
    return {
      canAnimate,
      scale,
      addScrollListener: (callback) => {
        const id = Math.random().toString();
        scrollListeners.current.set(id, {
          id,
          callback,
        });
        return () => {
          scrollListeners.current.delete(id);
        };
      },
      addPathUpdateListener: (target, callback) => {
        const id = Math.random().toString();
        pathUpdateListeners.current.set(id, {
          id,
          target,
          callback,
        });
        resizeObserverRef.current.observe(target);
        return () => {
          pathUpdateListeners.current.delete(id);
        };
      },
    };
  }, []);
};
